import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import { Plus, Prohibit, ArrowClockwise, PencilSimple, Trash, CheckCircle, ArrowsClockwise } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CLASS = {
  draft:     'bg-blue-50 text-blue-800 border-blue-200',
  finalized: 'bg-indigo-50 text-indigo-800 border-indigo-200',
  paid:      'bg-emerald-50 text-emerald-800 border-emerald-200',
  partial:   'bg-amber-50 text-amber-800 border-amber-200',
  unpaid:    'bg-red-50 text-red-800 border-red-200',
  voided:    'bg-zinc-100 text-zinc-500 border-zinc-300 line-through',
};

const fmtMoney = (n) => `€${Number(n || 0).toFixed(2)}`;
const fmtDate = (d) => {
  if (!d || d.length < 10) return d || '';
  return `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
};

const MONTH_LABELS_EN = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTH_LABELS_EL = [
  '', 'Ιανουάριος', 'Φεβρουάριος', 'Μάρτιος', 'Απρίλιος', 'Μάιος', 'Ιούνιος',
  'Ιούλιος', 'Αύγουστος', 'Σεπτέμβριος', 'Οκτώβριος', 'Νοέμβριος', 'Δεκέμβριος',
];

/**
 * Phase 735.44 — Cleaner Invoices tab. Shows the invoice ledger for a
 * single cleaner, with a "Create Invoice" flow that previews the auto-
 * generated line items for a selected month before persisting. Voided
 * invoices can be surfaced with the "Show voided" toggle.
 *
 * Props:
 *   cleanerId (string) — required
 *   language  (string) — 'en' | 'el'
 *   t         (fn)     — translation getter
 *   axCfg     (object) — axios request config (auth headers)
 *   onChange  (fn)     — called after any mutation so parent can refresh
 *                        dashboard totals.
 */
export const CleanerInvoicesTab = ({ cleanerId, language, t, axCfg, onChange }) => {
  const [data, setData] = useState({ invoices: [], total_invoiced: 0, total_paid: 0, outstanding: 0 });
  const [loading, setLoading] = useState(false);
  const [includeVoided, setIncludeVoided] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null); // { period_month, line_items, total, existing_invoice }
  const monthDefault = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  })();
  const [previewMonth, setPreviewMonth] = useState(monthDefault);
  const [description, setDescription] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [voidingId, setVoidingId] = useState(null);
  // Phase 735.45 — Full-lifecycle Edit dialog. `editingInvoice` holds
  // the full invoice doc; the dialog forks its own draft state so the
  // user can revert by cancelling. Rebuild opens a nested confirmation
  // dialog when the invoice is finalized/partial/paid.
  const [editingInvoice, setEditingInvoice] = useState(null);

  const fetchInvoices = useCallback(async () => {
    if (!cleanerId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({ include_voided: includeVoided ? 'true' : 'false' });
      const res = await axios.get(`${API}/cleaning/cleaners/${cleanerId}/invoices?${qs.toString()}`, axCfg);
      setData(res.data || { invoices: [], total_invoiced: 0, total_paid: 0, outstanding: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [cleanerId, includeVoided, axCfg]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const runPreview = useCallback(async (month) => {
    setPreviewLoading(true);
    setPreview(null);
    try {
      const res = await axios.get(
        `${API}/cleaning/cleaners/${cleanerId}/invoices/preview?month=${encodeURIComponent(month)}`,
        axCfg,
      );
      setPreview(res.data);
      // Prefill sensible defaults.
      setDescription(res.data?.description || `Cleaning services — ${month}`);
      setInvoiceDate(res.data?.period_end || '');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to build preview');
    } finally {
      setPreviewLoading(false);
    }
  }, [cleanerId, axCfg]);

  const openCreateDialog = () => {
    setCreateOpen(true);
    setPreviewMonth(monthDefault);
    runPreview(monthDefault);
  };

  const monthOptions = useMemo(() => {
    // Last 24 months + next 3 months.
    const out = [];
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() + 3);
    for (let i = 0; i < 27; i++) {
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      const key = `${y}-${String(m).padStart(2, '0')}`;
      const label = language === 'el' ? `${MONTH_LABELS_EL[m]} ${y}` : `${MONTH_LABELS_EN[m]} ${y}`;
      out.push({ key, label });
      d.setMonth(d.getMonth() - 1);
    }
    return out;
  }, [language]);

  const submitCreate = async () => {
    if (!preview) return;
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/cleaning/cleaners/${cleanerId}/invoices`,
        { month: previewMonth, description, invoice_date: invoiceDate },
        axCfg,
      );
      toast.success(t('invoiceCreated') || `Invoice ${res.data?.invoice_number} created`);
      setCreateOpen(false);
      setPreview(null);
      await fetchInvoices();
      onChange?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'invoice_exists') {
        toast.error(t('invoiceExists') || `Invoice ${detail.invoice_number} already exists for this month.`);
      } else {
        toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Failed to create invoice');
      }
    } finally {
      setSaving(false);
    }
  };

  const voidInvoice = async (inv) => {
    if (!window.confirm((t('confirmVoidInvoice') || 'Void this invoice?') + `\n${inv.invoice_number}`)) return;
    setVoidingId(inv.invoice_id);
    try {
      await axios.post(`${API}/cleaning/cleaners/${cleanerId}/invoices/${inv.invoice_id}/void`, {}, axCfg);
      toast.success(t('invoiceVoided') || 'Invoice voided');
      await fetchInvoices();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to void invoice');
    } finally {
      setVoidingId(null);
    }
  };

  const reactivateInvoice = async (inv) => {
    try {
      await axios.post(`${API}/cleaning/cleaners/${cleanerId}/invoices/${inv.invoice_id}/reactivate`, {}, axCfg);
      toast.success(t('invoiceReactivated') || 'Invoice reactivated');
      await fetchInvoices();
      onChange?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Failed to reactivate');
    }
  };

  return (
    <div className="space-y-3" data-testid={`cleaner-invoices-tab-${cleanerId}`}>
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 text-xs text-zinc-600">
          <label className="inline-flex items-center gap-1.5 cursor-pointer" data-testid="cleaner-invoices-show-voided-label">
            <input
              type="checkbox"
              className="accent-zinc-900"
              checked={includeVoided}
              onChange={(e) => setIncludeVoided(e.target.checked)}
              data-testid="cleaner-invoices-show-voided"
            />
            <span>{t('showVoided') || 'Show voided'}</span>
          </label>
        </div>
        <Button
          size="sm"
          className="rounded-none h-8 text-xs"
          onClick={openCreateDialog}
          data-testid="create-cleaner-invoice-btn"
        >
          <Plus size={14} className="mr-1" /> {t('createInvoice') || 'Create Invoice'}
        </Button>
      </div>

      {/* Table */}
      <div className="border border-zinc-200 bg-white overflow-x-auto" data-testid="cleaner-invoices-table-wrap">
        <table className="w-full text-sm" data-testid="cleaner-invoices-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-200 bg-zinc-50">
              <th className="text-left px-3 py-2">{t('invoiceNumber') || 'Invoice #'}</th>
              <th className="text-left px-3 py-2">{t('invoiceDate') || 'Invoice Date'}</th>
              <th className="text-left px-3 py-2">{t('period') || 'Period'}</th>
              <th className="text-left px-3 py-2">{t('description') || 'Description'}</th>
              <th className="text-right px-3 py-2">{t('total') || 'Total'}</th>
              <th className="text-right px-3 py-2">{t('paid') || 'Paid'}</th>
              <th className="text-right px-3 py-2">{t('balance') || 'Balance'}</th>
              <th className="text-center px-3 py-2">{t('status') || 'Status'}</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="text-center py-6 text-zinc-400 text-xs">{t('loading') || 'Loading…'}</td></tr>
            )}
            {!loading && data.invoices.length === 0 && (
              <tr><td colSpan={9} className="text-center py-8 text-zinc-400 text-xs" data-testid="cleaner-invoices-empty">
                {t('noInvoicesYet') || 'No invoices yet. Click "Create Invoice" to generate one for a month.'}
              </td></tr>
            )}
            {!loading && data.invoices.map((inv) => {
              const cls = STATUS_CLASS[inv.status] || STATUS_CLASS.draft;
              const isVoid = inv.status === 'voided';
              return (
                <tr key={inv.invoice_id} className="border-b border-zinc-100 hover:bg-zinc-50/60"
                    data-testid={`cleaner-invoice-row-${inv.invoice_id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{inv.invoice_number}</td>
                  <td className="px-3 py-2 font-mono text-xs">{fmtDate(inv.invoice_date)}</td>
                  <td className="px-3 py-2 text-xs">{inv.period_month}</td>
                  {/* Phase 735.45 — Show the RAW stored `description`
                      exactly as saved. NEVER regenerate from jobs. */}
                  <td className="px-3 py-2 text-xs text-zinc-700 max-w-[280px] truncate"
                      title={inv.description || ''}
                      data-testid={`cleaner-invoice-description-${inv.invoice_id}`}>
                    {inv.description || <span className="text-zinc-400 italic">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums">{fmtMoney(inv.total)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-700">{fmtMoney(inv.paid_amount)}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold">
                    {isVoid ? '—' : fmtMoney(inv.balance ?? (inv.total - (inv.paid_amount || 0)))}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 border ${cls}`}
                          data-testid={`cleaner-invoice-status-${inv.invoice_id}`}>
                      {t(`cleanerInvoiceStatus_${inv.status}`) || inv.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {!isVoid && (
                      <button
                        onClick={() => setEditingInvoice(inv)}
                        title={t('edit') || 'Edit'}
                        className="inline-flex items-center justify-center h-7 w-7 text-zinc-600 hover:bg-zinc-100"
                        data-testid={`cleaner-invoice-edit-${inv.invoice_id}`}
                      ><PencilSimple size={14} /></button>
                    )}
                    {isVoid ? (
                      <button
                        onClick={() => reactivateInvoice(inv)}
                        title={t('reactivate') || 'Reactivate'}
                        className="inline-flex items-center justify-center h-7 w-7 text-emerald-600 hover:bg-emerald-50"
                        data-testid={`cleaner-invoice-reactivate-${inv.invoice_id}`}
                      ><ArrowClockwise size={14} /></button>
                    ) : (
                      <button
                        onClick={() => voidInvoice(inv)}
                        disabled={voidingId === inv.invoice_id}
                        title={t('void') || 'Void'}
                        className="inline-flex items-center justify-center h-7 w-7 text-red-500 hover:bg-red-50 disabled:opacity-40"
                        data-testid={`cleaner-invoice-void-${inv.invoice_id}`}
                      ><Prohibit size={14} /></button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit Invoice dialog — Phase 735.45 */}
      <CleanerInvoiceEditDialog
        cleanerId={cleanerId}
        invoice={editingInvoice}
        axCfg={axCfg}
        t={t}
        onClose={() => setEditingInvoice(null)}
        onSaved={async () => {
          setEditingInvoice(null);
          await fetchInvoices();
          onChange?.();
        }}
      />

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-3xl" data-testid="create-cleaner-invoice-dialog">
          <DialogHeader>
            <DialogTitle>{t('createInvoice') || 'Create Invoice'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{t('month') || 'Month'}</Label>
                <Select value={previewMonth} onValueChange={(v) => { setPreviewMonth(v); runPreview(v); }}>
                  <SelectTrigger className="rounded-none h-9 text-sm" data-testid="cleaner-invoice-month-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {monthOptions.map((mo) => (
                      <SelectItem key={mo.key} value={mo.key}>{mo.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{t('invoiceDate') || 'Invoice Date'}</Label>
                <Input
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                  className="rounded-none h-9 text-sm"
                  data-testid="cleaner-invoice-date-input"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{t('invoiceNumber') || 'Invoice #'}</Label>
                <div className="text-sm font-mono text-zinc-500 h-9 flex items-center px-2 border border-dashed border-zinc-200 bg-zinc-50" data-testid="cleaner-invoice-number-preview">
                  {t('autoAssigned') || 'Auto-assigned on Save'}
                </div>
              </div>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{t('description') || 'Description'}</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                className="rounded-none text-sm"
                data-testid="cleaner-invoice-description-input"
              />
            </div>
            {preview?.existing_invoice && (
              <div className="border border-amber-300 bg-amber-50 text-amber-800 text-xs p-2" data-testid="cleaner-invoice-existing-warn">
                {t('invoiceExistsWarning') || `An active invoice ${preview.existing_invoice.invoice_number} already exists for this month.`}
              </div>
            )}

            <div className="border border-zinc-200 max-h-[280px] overflow-y-auto" data-testid="cleaner-invoice-preview-lines">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-200 bg-zinc-50">
                    <th className="text-left px-2 py-1.5">{t('date') || 'Date'}</th>
                    <th className="text-left px-2 py-1.5">{t('description') || 'Description'}</th>
                    <th className="text-right px-2 py-1.5">{t('quantity') || 'Qty'}</th>
                    <th className="text-right px-2 py-1.5">{t('rate') || 'Rate'}</th>
                    <th className="text-right px-2 py-1.5">{t('amount') || 'Amount'}</th>
                  </tr>
                </thead>
                <tbody>
                  {previewLoading && (
                    <tr><td colSpan={5} className="text-center py-4 text-zinc-400">{t('loading') || 'Loading…'}</td></tr>
                  )}
                  {!previewLoading && preview && preview.line_items.length === 0 && (
                    <tr><td colSpan={5} className="text-center py-4 text-zinc-400">{t('noWorkThisMonth') || 'No work found for this month.'}</td></tr>
                  )}
                  {!previewLoading && preview && preview.line_items.map((l) => (
                    <tr key={l.line_id} className="border-b border-zinc-100" data-testid={`preview-line-${l.line_id}`}>
                      <td className="px-2 py-1 font-mono">{fmtDate(l.date)}</td>
                      <td className="px-2 py-1">{l.description}</td>
                      <td className="px-2 py-1 text-right font-mono">{Number(l.quantity || 0).toFixed(2)}</td>
                      <td className="px-2 py-1 text-right font-mono">{fmtMoney(l.rate)}</td>
                      <td className="px-2 py-1 text-right font-mono">{fmtMoney(l.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                {preview && (
                  <tfoot>
                    <tr className="border-t-2 border-zinc-300 bg-zinc-50 font-semibold">
                      <td colSpan={4} className="px-2 py-1.5 text-right uppercase text-[10px] tracking-wider">{t('total') || 'Total'}</td>
                      <td className="px-2 py-1.5 text-right font-mono tabular-nums" data-testid="cleaner-invoice-preview-total">{fmtMoney(preview.total)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setCreateOpen(false)} data-testid="cleaner-invoice-cancel-btn">
              {t('cancel') || 'Cancel'}
            </Button>
            <Button
              className="rounded-none"
              onClick={submitCreate}
              disabled={saving || previewLoading || !preview || preview.line_items.length === 0 || !!preview.existing_invoice}
              data-testid="cleaner-invoice-save-btn"
            >
              {saving ? (t('saving') || 'Saving…') : (t('createInvoice') || 'Create Invoice')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const DashboardCell = ({ label, value, testid, valueClass = 'text-zinc-900' }) => (
  <div className="bg-white px-3 py-2 border-r border-zinc-100 last:border-r-0">
    <div className="text-[9px] uppercase tracking-wider text-zinc-400 font-semibold">{label}</div>
    <div className={`text-lg font-mono font-bold tabular-nums ${valueClass}`} data-testid={testid}>
      €{Number(value || 0).toFixed(2)}
    </div>
  </div>
);

/**
 * Phase 735.45 — Cleaner Invoice Edit Dialog.
 * Full CRUD on line items + editable header (date, description, notes).
 * Client-side total recalc mirrors the backend's rules so the user sees
 * the final total instantly. Rebuild-from-source shows a diff dialog
 * on non-draft invoices before committing.
 */
const CleanerInvoiceEditDialog = ({ cleanerId, invoice, axCfg, t, onClose, onSaved }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [rebuildPreview, setRebuildPreview] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (!invoice) { setForm(null); return; }
    setForm({
      invoice_date: invoice.invoice_date || '',
      description: invoice.description || '',
      notes: invoice.notes || '',
      line_items: (invoice.line_items || []).map((l) => ({ ...l })),
    });
    setRebuildPreview(null);
    setShowHistory(false);
  }, [invoice]);

  const total = useMemo(() => {
    if (!form) return 0;
    return round2(form.line_items.reduce((s, l) => s + Number(l.amount || 0), 0));
  }, [form]);

  if (!invoice || !form) return null;

  const isVoid = invoice.status === 'voided';
  const isLocked = ['partial', 'paid'].includes(invoice.status);

  const updateLine = (idx, key, value) => {
    setForm((prev) => {
      const next = { ...prev, line_items: prev.line_items.map((l, i) => i === idx ? { ...l } : l) };
      const row = next.line_items[idx];
      if (key === 'quantity' || key === 'rate') {
        row[key] = value === '' ? '' : Number(value);
        const q = Number(row.quantity || 0);
        const r = Number(row.rate || 0);
        row.amount = round2(q * r);
      } else if (key === 'amount') {
        row.amount = value === '' ? 0 : round2(Number(value));
      } else {
        row[key] = value;
      }
      return next;
    });
  };

  const addLine = () => {
    setForm((prev) => ({
      ...prev,
      line_items: [
        ...prev.line_items,
        {
          line_id: `new_${Math.random().toString(36).slice(2, 10)}`,
          type: 'manual',
          source_id: '',
          date: invoice.period_start || '',
          description: '',
          quantity: 1,
          rate: 0,
          amount: 0,
        },
      ],
    }));
  };

  const removeLine = (idx) => {
    setForm((prev) => ({ ...prev, line_items: prev.line_items.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        invoice_date: form.invoice_date,
        description: form.description,
        notes: form.notes,
        line_items: form.line_items.map((l) => ({
          line_id: l.line_id,
          type: l.type || 'manual',
          source_id: l.source_id || '',
          date: l.date || '',
          description: l.description || '',
          quantity: Number(l.quantity || 0),
          rate: Number(l.rate || 0),
          amount: Number(l.amount || 0),
        })),
      };
      if (isLocked) payload.force = true;
      await axios.patch(`${API}/cleaning/cleaners/${cleanerId}/invoices/${invoice.invoice_id}`, payload, axCfg);
      toast.success(t('invoiceUpdated') || 'Invoice updated');
      onSaved?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Failed to update invoice');
    } finally { setSaving(false); }
  };

  const finalize = async () => {
    if (!window.confirm(t('confirmFinalize') || 'Finalize this invoice? Automatic sync from Jobs will be disabled.')) return;
    try {
      await axios.post(`${API}/cleaning/cleaners/${cleanerId}/invoices/${invoice.invoice_id}/finalize`, {}, axCfg);
      toast.success(t('invoiceFinalized') || 'Invoice finalized');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to finalize');
    }
  };

  const openRebuild = async () => {
    try {
      const res = await axios.post(
        `${API}/cleaning/cleaners/${cleanerId}/invoices/${invoice.invoice_id}/rebuild-preview`,
        {}, axCfg,
      );
      setRebuildPreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to compute rebuild preview');
    }
  };

  const doRebuild = async () => {
    setRebuilding(true);
    try {
      await axios.post(
        `${API}/cleaning/cleaners/${cleanerId}/invoices/${invoice.invoice_id}/rebuild`,
        { confirm: true }, axCfg,
      );
      toast.success(t('invoiceRebuilt') || 'Invoice rebuilt from Jobs');
      onSaved?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Failed to rebuild');
    } finally { setRebuilding(false); setRebuildPreview(null); }
  };

  return (
    <Dialog open={!!invoice} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-4xl" data-testid="edit-cleaner-invoice-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>{t('editInvoice') || 'Edit Invoice'} — {invoice.invoice_number}</span>
            <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 border ${STATUS_CLASS[invoice.status] || STATUS_CLASS.draft}`}>
              {t(`cleanerInvoiceStatus_${invoice.status}`) || invoice.status}
            </span>
            {isLocked && (
              <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-300 px-1.5 py-0.5" data-testid="edit-invoice-locked-badge">
                {t('lockedByPayments') || 'LOCKED BY PAYMENTS'}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {isLocked && (
          <div className="border border-amber-300 bg-amber-50 text-amber-800 text-xs p-2" data-testid="edit-invoice-locked-warn">
            {t('editInvoiceLockedWarn') || 'This invoice has allocated payments. Line-item edits will be marked as a forced override in the audit trail.'}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{t('invoiceDate') || 'Invoice Date'}</Label>
            <Input
              type="date"
              value={form.invoice_date || ''}
              onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
              className="rounded-none h-9 text-sm"
              data-testid="edit-invoice-date"
              disabled={isVoid}
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{t('description') || 'Description'}</Label>
            <Input
              value={form.description || ''}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="rounded-none h-9 text-sm"
              placeholder={t('descriptionPlaceholder') || 'e.g. MAY 6+13+20+27'}
              data-testid="edit-invoice-description"
              disabled={isVoid}
            />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">{t('notes') || 'Notes'}</Label>
          <Textarea
            value={form.notes || ''}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={2}
            className="rounded-none text-sm"
            data-testid="edit-invoice-notes"
            disabled={isVoid}
          />
        </div>

        <div className="border border-zinc-200 max-h-[300px] overflow-y-auto" data-testid="edit-invoice-lines">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-200 bg-zinc-50">
                <th className="text-left px-2 py-1.5 w-24">{t('date') || 'Date'}</th>
                <th className="text-left px-2 py-1.5">{t('description') || 'Description'}</th>
                <th className="text-right px-2 py-1.5 w-16">{t('quantity') || 'Qty'}</th>
                <th className="text-right px-2 py-1.5 w-20">{t('rate') || 'Rate'}</th>
                <th className="text-right px-2 py-1.5 w-24">{t('amount') || 'Amount'}</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {form.line_items.map((l, idx) => (
                <tr key={l.line_id || idx} className="border-b border-zinc-100" data-testid={`edit-invoice-line-${idx}`}>
                  <td className="px-1 py-1">
                    <Input type="date" value={l.date || ''} onChange={(e) => updateLine(idx, 'date', e.target.value)}
                           className="rounded-none h-7 text-[11px]" disabled={isVoid} data-testid={`edit-line-date-${idx}`} />
                  </td>
                  <td className="px-1 py-1">
                    <Input value={l.description || ''} onChange={(e) => updateLine(idx, 'description', e.target.value)}
                           className="rounded-none h-7 text-[11px]" disabled={isVoid}
                           data-testid={`edit-line-desc-${idx}`} />
                  </td>
                  <td className="px-1 py-1">
                    <Input type="number" step="0.01" value={l.quantity ?? ''} onChange={(e) => updateLine(idx, 'quantity', e.target.value)}
                           className="rounded-none h-7 text-[11px] text-right font-mono" disabled={isVoid}
                           data-testid={`edit-line-qty-${idx}`} />
                  </td>
                  <td className="px-1 py-1">
                    <Input type="number" step="0.01" value={l.rate ?? ''} onChange={(e) => updateLine(idx, 'rate', e.target.value)}
                           className="rounded-none h-7 text-[11px] text-right font-mono" disabled={isVoid}
                           data-testid={`edit-line-rate-${idx}`} />
                  </td>
                  <td className="px-1 py-1">
                    <Input type="number" step="0.01" value={l.amount ?? ''} onChange={(e) => updateLine(idx, 'amount', e.target.value)}
                           className="rounded-none h-7 text-[11px] text-right font-mono font-semibold" disabled={isVoid}
                           data-testid={`edit-line-amount-${idx}`} />
                  </td>
                  <td className="px-1 py-1 text-center">
                    <button onClick={() => removeLine(idx)} disabled={isVoid || form.line_items.length <= 1}
                            className="text-red-500 hover:bg-red-50 disabled:opacity-30 p-1"
                            data-testid={`edit-line-remove-${idx}`}
                            title={t('delete') || 'Delete'}><Trash size={12} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-zinc-300 bg-zinc-50 font-semibold">
                <td className="px-2 py-1.5">
                  <Button onClick={addLine} size="sm" variant="outline" className="h-7 text-[10px] rounded-none"
                          disabled={isVoid} data-testid="edit-invoice-add-line">
                    <Plus size={12} className="mr-1" /> {t('addLine') || 'Add Line'}
                  </Button>
                </td>
                <td colSpan={3} className="px-2 py-1.5 text-right uppercase text-[10px] tracking-wider">
                  {t('total') || 'Total'}
                </td>
                <td className="px-2 py-1.5 text-right font-mono tabular-nums text-sm" data-testid="edit-invoice-total">
                  €{total.toFixed(2)}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Audit trail collapsible */}
        {invoice.history && invoice.history.length > 0 && (
          <div>
            <button onClick={() => setShowHistory((v) => !v)}
                    className="text-[10px] uppercase tracking-wider text-zinc-500 hover:text-zinc-800"
                    data-testid="edit-invoice-history-toggle">
              {showHistory ? '▾' : '▸'} {t('auditTrail') || 'Audit Trail'} ({invoice.history.length})
            </button>
            {showHistory && (
              <div className="mt-2 border border-zinc-200 bg-zinc-50 text-[11px] max-h-40 overflow-y-auto"
                   data-testid="edit-invoice-history">
                {invoice.history.map((h, i) => (
                  <div key={i} className="border-b border-zinc-100 px-2 py-1 last:border-b-0">
                    <span className="text-zinc-400 font-mono mr-2">{(h.at || '').slice(0, 19).replace('T', ' ')}</span>
                    <span className="font-semibold text-zinc-700 mr-2">[{h.event}]</span>
                    <span className="text-zinc-600">{h.detail}</span>
                    <span className="text-zinc-400 ml-2">— {h.by}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="flex items-center justify-between gap-2 w-full">
          <div className="flex items-center gap-2">
            {!isVoid && invoice.status === 'draft' && (
              <Button variant="outline" className="rounded-none h-8 text-xs" onClick={finalize}
                      data-testid="edit-invoice-finalize-btn">
                <CheckCircle size={14} className="mr-1" /> {t('finalize') || 'Finalize'}
              </Button>
            )}
            {!isVoid && (
              <Button variant="outline" className="rounded-none h-8 text-xs" onClick={openRebuild}
                      data-testid="edit-invoice-rebuild-btn">
                <ArrowsClockwise size={14} className="mr-1" /> {t('rebuildFromJobs') || 'Rebuild from Jobs'}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-none h-8" onClick={onClose} data-testid="edit-invoice-cancel">
              {t('cancel') || 'Cancel'}
            </Button>
            <Button className="rounded-none h-8" onClick={save} disabled={saving || isVoid}
                    data-testid="edit-invoice-save">
              {saving ? (t('saving') || 'Saving…') : (t('save') || 'Save')}
            </Button>
          </div>
        </DialogFooter>

        {/* Nested rebuild-confirmation dialog */}
        <Dialog open={!!rebuildPreview} onOpenChange={(v) => { if (!v) setRebuildPreview(null); }}>
          <DialogContent className="max-w-2xl" data-testid="rebuild-invoice-dialog">
            <DialogHeader>
              <DialogTitle>{t('rebuildInvoice') || 'Rebuild Invoice from Jobs'}</DialogTitle>
            </DialogHeader>
            {rebuildPreview && (
              <div className="space-y-2 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div className="border border-zinc-200 p-2">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t('currentTotal') || 'Current Total'}</div>
                    <div className="font-mono text-lg font-bold">€{Number(rebuildPreview.current_total || 0).toFixed(2)}</div>
                  </div>
                  <div className="border border-zinc-200 p-2 bg-blue-50">
                    <div className="text-[10px] uppercase tracking-wider text-blue-700">{t('proposedTotal') || 'Proposed Total'}</div>
                    <div className="font-mono text-lg font-bold text-blue-800">€{Number(rebuildPreview.proposed_total || 0).toFixed(2)}</div>
                  </div>
                </div>
                <div className="text-xs space-y-1" data-testid="rebuild-diff">
                  <div>+ {rebuildPreview.added.length} {t('addedLines') || 'lines to add'}</div>
                  <div>− {rebuildPreview.removed.length} {t('removedLines') || 'lines to remove'}</div>
                  <div>≈ {rebuildPreview.changed.length} {t('changedLines') || 'lines changed'}</div>
                  <div className="text-zinc-500">{rebuildPreview.manual_lines_kept.length} {t('manualLinesKept') || 'manual line(s) kept as-is'}</div>
                </div>
                {invoice.status !== 'draft' && (
                  <div className="border border-red-300 bg-red-50 text-red-800 text-xs p-2">
                    ⚠ {t('rebuildWarning') || 'This invoice is not a draft. Rebuilding may affect allocations and balances.'}
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setRebuildPreview(null)}
                      data-testid="rebuild-cancel">
                {t('cancel') || 'Cancel'}
              </Button>
              <Button className="rounded-none" onClick={doRebuild} disabled={rebuilding}
                      data-testid="rebuild-confirm">
                {rebuilding ? (t('rebuilding') || 'Rebuilding…') : (t('confirmRebuild') || 'Confirm Rebuild')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;



/**
 * Phase 735.44 — Small dashboard strip: Total Invoiced / Total Paid /
 * Outstanding Balance. Renders at the top of the cleaner detail panel.
 */
export const CleanerInvoiceDashboard = ({ cleanerId, refreshKey, axCfg, t }) => {
  const [data, setData] = useState({ total_invoiced: 0, total_paid: 0, outstanding: 0, invoice_count: 0 });
  useEffect(() => {
    if (!cleanerId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/cleaning/cleaners/${cleanerId}/dashboard-totals`, axCfg);
        if (!cancelled) setData(res.data);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [cleanerId, refreshKey, axCfg]);
  const outstandingPositive = Number(data.outstanding || 0) > 0.005;

  return (
    <div className="grid grid-cols-3 border border-zinc-200 bg-zinc-100" data-testid="cleaner-invoice-dashboard">
      <DashboardCell label={t('totalInvoiced') || 'Total Invoiced'} value={data.total_invoiced} testid="cleaner-dashboard-invoiced" />
      <DashboardCell label={t('totalPaid') || 'Total Paid'} value={data.total_paid} testid="cleaner-dashboard-paid" valueClass="text-emerald-700" />
      <DashboardCell
        label={t('outstandingBalance') || 'Outstanding Balance'}
        value={data.outstanding}
        testid="cleaner-dashboard-outstanding"
        valueClass={outstandingPositive ? 'text-red-700' : 'text-emerald-700'}
      />
    </div>
  );
};

export default CleanerInvoicesTab;
